import { alpha, Box, Stack, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { MuiColorInput } from 'mui-color-input';
import { DEFAULT_PALETTE } from 'hooks/canvas/useSymbolGroups';

interface GroupColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  /**
   * Raise the hex picker's gradient popover above other layers. Needed inside
   * the inline toolbar popover (which itself sits at modal+1), so the gradient
   * isn't tucked behind it.
   */
  paletteInFront?: boolean;
}

/**
 * Group color chooser: preset swatch grid + a hex input. Shared by the create
 * dialog, the inline color popover, and the mobile edit sheet.
 */
export const GroupColorPicker = ({
  value,
  onChange,
  paletteInFront = false,
}: GroupColorPickerProps) => {
  const theme = useTheme();
  return (
    <Stack sx={{ gap: 4 }}>
      <Stack sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: 3 }}>
        {DEFAULT_PALETTE.map((swatch) => {
          const isSelected = swatch.toLowerCase() === value.toLowerCase();
          return (
            <Box
              data-ph-capture-attribute-element="canvas-group-color-pick"
              key={swatch}
              onClick={() => onChange(swatch)}
              role="button"
              aria-label={`Pick color ${swatch}`}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                backgroundColor: swatch,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isSelected
                  ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${swatch}`
                  : 'none',
                transition:
                  'transform 130ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 130ms',
                '&:hover': { transform: 'scale(1.12)' },
              }}
            >
              {isSelected && (
                <CheckIcon
                  sx={{
                    fontSize: 16,
                    color: theme.palette.common.white,
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Stack>

      <MuiColorInput
        format="hex"
        value={value}
        onChange={(v: string) => onChange(v)}
        isAlphaHidden
        size="small"
        fullWidth
        placeholder="#RRGGBB"
        PopoverProps={
          paletteInFront
            ? { sx: { zIndex: theme.zIndex.modal + 2 } }
            : undefined
        }
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
            fontSize: 13,
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
            letterSpacing: 0.3,
            '& fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.5),
            },
            '&:hover fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.8),
            },
          },
        }}
      />
    </Stack>
  );
};

export default GroupColorPicker;
